import { useEffect } from "react";
import type { GetServerSideProps, InferGetServerSidePropsType } from "next";
import { useRouter } from "next/router";
import { useSession } from "@lululemon/auth-sdk";
import { getServerSideConfig } from "../../lib/get-server-side-config";
import {
  getWebstringsBySets,
  WebStringProvider
} from "@lululemon/alpine-ecom-contentful";
import {
  pageRelativeUrls,
  TrackOrderLanding
} from "@lululemon/ecom-post-purchase";
import { CreateSSRClient } from "@lululemon/ecom-graphql";
import { ssrLogger } from "../../lib/error-handling/ssr-logger";

type WebstringsResult =
  | { webStrings: Record<string, unknown>[]; messages: Record<string, string> }
  | { errors: unknown[] };

type TrackOrderServerSideProps = {
  errors?: string | null;
  locale: string;
  messages: Record<string, string>;
  initialOrderId: string;
};

export const getServerSideProps = (async (context) => {
  const { query, req, locale } = context;
  const preview = query?.preview;
  const initialOrderId = typeof query?.id === "string" ? query.id : "";
  const configs = getServerSideConfig(context);

  const ssrClient = await CreateSSRClient(configs, req);

  try {
    const result: WebstringsResult = await getWebstringsBySets({
      keys: ["track_order_return_page"],
      locale: locale,
      preview: preview === "1",
      client: ssrClient
    });

    if ("errors" in result) {
      ssrLogger("Failed to fetch webStrings", {
        message: result.errors
          .map((e) => (e as { message: string }).message)
          .join(", "),
        operationName: "getServerSideProps:purchases/track"
      });
      return {
        props: {
          errors: String(
            result.errors
              .map((e) => (e as { message: string }).message)
              .join(", ")
          ),
          locale,
          messages: {},
          initialOrderId
        }
      };
    }

    return {
      props: {
        errors: null,
        locale,
        messages: result.messages ?? {},
        initialOrderId
      }
    };
  } catch (error) {
    ssrLogger(
      "Failed to fetch webStrings",
      error instanceof Error
        ? { name: error.name, message: error.message, stack: error.stack }
        : { message: "Unknown error" }
    );
    return {
      props: {
        errors: error instanceof Error ? error.message : "Unknown error",
        locale,
        messages: {},
        initialOrderId
      }
    };
  }
}) satisfies GetServerSideProps<TrackOrderServerSideProps>;

export default function TrackOrderPage(
  data: InferGetServerSidePropsType<typeof getServerSideProps>
): React.ReactElement {
  const { locale, messages, initialOrderId } = data || {};
  const router = useRouter();
  // Stay put while an auth flow is mid-step (e.g. the signin drawer's set a new
  // password) so the guest isn't navigated away before they can finish.
  // isFullyAuthenticated (not authenticationStatus) so a partially-authenticated
  // session isn't treated as logged in.
  const { isAuthSettled = true, isFullyAuthenticated: isAuthenticated } =
    useSession();
  const shouldRedirect = isAuthenticated && isAuthSettled;

  useEffect(() => {
    if (shouldRedirect) {
      router.push(pageRelativeUrls.account.purchaseHistory);
    }
  }, [shouldRedirect, router]);

  // return null while redirecting to avoid flashing the track order page for authenticated users
  if (shouldRedirect) {
    return null;
  }

  return (
    <WebStringProvider locale={locale} messages={messages}>
      <TrackOrderLanding locale={locale} initialOrderId={initialOrderId} />
    </WebStringProvider>
  );
}
